import { Injectable, OnDestroy, inject } from '@angular/core';
import {
  GameClientEvents,
  GameServerEvents,
  ReadyClientEvents,
  ReadyServerEvents,
  ReconnectDto,
  ReconnectSuccessDto,
  RoomClientEvents,
  RoomServerEvents,
  SocketService,
  SocketConfig,
  ConnectionStatus,
  GameState,
  Direction,
  AttackData,
  PowerUpData,
  GameData,
  MatchEndResult,
  MatchStartDto,
  CreateRoomDto,
  JoinRoomDto,
  VerifyRoomPasswordDto,
  LeaveRoomDto,
  RoomStateDto,
  PlayerRoomEventDto,
  GameSettings,
} from '@snake/shared';
import { Observable, Subject } from 'rxjs';
import { environment } from '../../environments/environment';
import { UsernameStorageService } from './username-storage.service';

const TOKEN_KEY = 'snk_player_token';
const LAST_ROOM_KEY = 'snk_last_room';
const MAX_RECONNECT_ATTEMPTS = 5;
const RECONNECT_INITIAL_DELAY_MS = 1_000;
const RECONNECT_BACKOFF_MULTIPLIER = 2;
const RECONNECT_MAX_DELAY_MS = 10_000;

@Injectable({
  providedIn: 'root',
})
export class GameSocketService implements OnDestroy {
  private socketService: SocketService;
  private clientId: string | null = null;
  private usernameStorage = inject(UsernameStorageService);

  // Auto-reconnect state
  private reconnectAttempts = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  /** Token received on the current connection — held in memory until we know whether to persist it. */
  private pendingNewToken: string | null = null;

  // Observable subjects for server events
  private gameStateUpdatesSubject = new Subject<GameState>();
  private serverTickSubject = new Subject<GameState>();
  private matchTimerSubject = new Subject<number>();
  private playerReadySubject = new Subject<{
    playerId: string;
    isReady: boolean;
  }>();
  private playerJoinedSubject = new Subject<PlayerRoomEventDto>();
  private playerLeftSubject = new Subject<PlayerRoomEventDto>();
  private attackIncomingSubject = new Subject<AttackData>();
  private powerUpUsedSubject = new Subject<PowerUpData>();
  private gameStartSubject = new Subject<GameData>();
  private gameOverSubject = new Subject<MatchEndResult>();
  private roomCreatedSubject = new Subject<RoomStateDto>();
  private roomJoinedSubject = new Subject<RoomStateDto>();
  private roomErrorSubject = new Subject<{ type: string; message: string }>();
  private roomsUpdatedSubject = new Subject<RoomStateDto[]>();
  private readyUpdateSubject = new Subject<{
    readyPlayers: Record<string, boolean>;
    allReady?: boolean;
  }>();
  private countdownStartSubject = new Subject<{ startsAt: number }>();
  private matchStartSubject = new Subject<MatchStartDto>();
  private rematchWaitingSubject = new Subject<{ requesters: string[] }>();
  private targetUpdateSubject = new Subject<{
    playerId: string;
    targetPlayerId: string;
  }>();
  private playerDisconnectedSubject = new Subject<PlayerRoomEventDto>();
  private playerReconnectedSubject = new Subject<PlayerRoomEventDto>();
  private reconnectSuccessSubject = new Subject<ReconnectSuccessDto>();
  private reconnectFailedSubject = new Subject<{ reason: string }>();
  private countdownCancelledSubject = new Subject<{
    roomId: string;
    room?: RoomStateDto;
  }>();

  constructor() {
    const config: SocketConfig = {
      url: environment.wsUrl,
      namespace: 'game',
      options: {
        autoConnect: true,
        transports: ['websocket', 'polling'],
        timeout: 20000,
      },
    };

    this.socketService = new SocketService(config);
  }

  // ===== CONNECTION METHODS =====

  connect(): Promise<void> {
    // this.socketService.connect() runs its Promise constructor synchronously,
    // which means io() is called and the socket object is created before this
    // line returns. We register session-critical listeners immediately on that
    // socket so they are in place before the WebSocket handshake completes and
    // before WELCOME / PLAYER_TOKEN can arrive (fixes a race condition where
    // both events fire in the same event-loop tick as 'connect').
    const connectPromise = this.socketService.connect();
    this.registerSessionListeners();
    return connectPromise
      .then(() => {
        this.setupEventListeners();
      })
      .catch((error) => {
        console.error('❌ Socket connection failed:', error);
        throw error;
      });
  }

  /**
   * Register WELCOME, PLAYER_TOKEN and disconnect listeners synchronously on
   * the current socket. Called right after io() creates the socket so that
   * events emitted during the handshake are never missed.
   */
  private registerSessionListeners(): void {
    const socket = this.socketService.getSocket();
    if (!socket) return;

    // Remove stale listeners so reconnect calls don't stack duplicates.
    socket.off(RoomServerEvents.WELCOME);
    socket.off(RoomServerEvents.PLAYER_TOKEN);
    socket.off('disconnect');

    socket.on(
      RoomServerEvents.WELCOME,
      (data: { clientId: string; message: string; timestamp: string }) => {
        console.log('🎉 Welcome from server:', data);
        this.clientId = data.clientId;
        console.log('💳 Client ID assigned:', this.clientId);
        const username = this.usernameStorage.getUsername();
        if (username) {
          socket.emit(RoomClientEvents.REGISTER_USERNAME, { username });
          console.log('🏷️ Registering username:', username);
        }
      }
    );

    socket.on(RoomServerEvents.PLAYER_TOKEN, (data: { token: string }) => {
      const existing = localStorage.getItem(TOKEN_KEY);
      if (!existing) {
        // Fresh session — persist immediately
        localStorage.setItem(TOKEN_KEY, data.token);
        console.log('🔑 Player token stored:', data.token);
      } else {
        // Reconnect scenario — keep the old token alive; remember the
        // new one in case the reconnect ultimately fails (terminal).
        this.pendingNewToken = data.token;
        console.log(
          '🔑 Existing token kept; pending new token saved in memory'
        );
      }
    });

    socket.on('disconnect', () => {
      this.startReconnectLoop();
    });
  }

  disconnect(): void {
    this.clientId = null;
    this.cancelReconnectLoop();
    this.socketService.disconnect();
  }

  isConnected(): boolean {
    return this.socketService.isConnected();
  }

  // ===== TOKEN / RECONNECT HELPERS =====

  /** Store the last room's joinCode so we can attempt reconnection. */
  setLastRoom(joinCode: string): void {
    localStorage.setItem(LAST_ROOM_KEY, joinCode);
  }

  /** Clear the stored last room (called on explicit leave). */
  clearLastRoom(): void {
    localStorage.removeItem(LAST_ROOM_KEY);
  }

  /**
   * Promote any pending (newer) server-issued token into localStorage.
   *
   * The server issues a fresh token on every socket connection, but the
   * PLAYER_TOKEN handler keeps the previously-stored token (so an in-flight
   * RECONNECT can still find the room). When the socket silently reconnects
   * (e.g. a dev server restart), the server's current token for this socket
   * is the newer one, and any room created/joined afterwards is bound to it.
   * Calling this right before binding to a room keeps localStorage in sync
   * with the room-bound token, so a later RECONNECT sends the correct token.
   */
  private commitPendingToken(): void {
    if (this.pendingNewToken) {
      localStorage.setItem(TOKEN_KEY, this.pendingNewToken);
      this.pendingNewToken = null;
      console.log('🔑 Committed pending token before room bind');
    }
  }

  /** Attempt to reconnect using stored token + joinCode. */
  emitReconnect(joinCode: string): void {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token || !joinCode) return;
    const payload: ReconnectDto = { token, joinCode };
    this.socketService.getSocket()?.emit(RoomClientEvents.RECONNECT, payload);
    console.log('📤 Emitting reconnect:', { joinCode });
  }

  /** Emit startGame event (host only). */
  emitStartGame(roomId: string): void {
    if (!this.socketService.isConnected()) return;
    this.socketService
      .getSocket()
      ?.emit(RoomClientEvents.START_GAME, { roomId });
    console.log('📤 Emitting startGame:', { roomId });
  }

  private startReconnectLoop(): void {
    const token = localStorage.getItem(TOKEN_KEY);
    const joinCode = localStorage.getItem(LAST_ROOM_KEY);
    if (!token || !joinCode) {
      // No stored state — nothing to reconnect to
      return;
    }

    this.reconnectAttempts = 0;
    this.scheduleReconnect(joinCode);
  }

  private scheduleReconnect(joinCode: string): void {
    if (this.reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
      console.warn('❌ Max reconnect attempts reached');
      this.reconnectFailedSubject.next({ reason: 'max_attempts' });
      return;
    }

    // Exponential backoff: initial 1s, doubling up to cap of 10s
    const delay = Math.min(
      RECONNECT_INITIAL_DELAY_MS *
        Math.pow(RECONNECT_BACKOFF_MULTIPLIER, this.reconnectAttempts),
      RECONNECT_MAX_DELAY_MS
    );
    this.reconnectAttempts++;
    console.log(
      `🔄 Reconnect attempt ${this.reconnectAttempts}/${MAX_RECONNECT_ATTEMPTS} in ${delay}ms`
    );

    this.reconnectTimer = setTimeout(async () => {
      try {
        // Use connect() which already calls setupEventListeners internally
        await this.connect();
        // After reconnecting, emit the RECONNECT event
        this.emitReconnect(joinCode);
      } catch {
        // Connection failed — try again
        this.scheduleReconnect(joinCode);
      }
    }, delay);
  }

  private cancelReconnectLoop(): void {
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.reconnectAttempts = 0;
  }

  /**
   * Get the client ID assigned by the server (socket.id)
   * Returns null if not connected or if welcome message hasn't been received yet
   */
  getClientId(): string | null {
    return this.clientId;
  }

  // ===== CLIENT TO SERVER EMISSIONS =====

  /**
   * Join a game room
   */
  joinGame(gameId: string, playerId: string): void {
    if (!this.socketService.isConnected()) {
      console.error('❌ Socket not connected, cannot join game');
      return;
    }

    const socket = this.socketService.getSocket()!;
    socket.emit(GameClientEvents.JOIN_GAME, { gameId, playerId });
    console.log('📤 Emitting joinGame:', { gameId, playerId });
  }

  /**
   * Leave the current game
   */
  leaveGame(): void {
    if (!this.socketService.isConnected()) {
      console.error('❌ Socket not connected, cannot leave game');
      return;
    }

    const socket = this.socketService.getSocket()!;
    socket.emit(GameClientEvents.LEAVE_GAME);
    console.log('📤 Emitting leaveGame');
  }

  /**
   * Send snake movement input
   */
  moveSnake(direction: Direction): void {
    if (!this.socketService.isConnected()) {
      console.error('❌ Socket not connected, cannot move snake');
      return;
    }

    const socket = this.socketService.getSocket()!;
    socket.emit(GameClientEvents.MOVE_SNAKE, { direction });
    console.log('📤 Emitting moveSnake:', direction);
  }

  /**
   * Send player input to the server
   * Used for real-time directional input during gameplay
   */
  sendPlayerInput(
    gameId: string,
    playerId: string,
    direction: Direction,
    sequence: number
  ): void {
    if (!this.socketService.isConnected()) {
      console.error('❌ Socket not connected, cannot send player input');
      return;
    }

    const socket = this.socketService.getSocket()!;
    socket.emit(GameClientEvents.PLAYER_INPUT, {
      gameId,
      playerId,
      direction,
      sequence,
      clientTimestamp: Date.now(),
    });
    console.log('📤 Emitting player_input:', {
      gameId,
      playerId,
      direction,
      sequence,
    });
  }

  /**
   * Use attack ability — fires one block at the attacker's current head position
   */
  useAttack(gameId: string, playerId: string, targetPlayerId?: string): void {
    if (!this.socketService.isConnected()) {
      console.error('❌ Socket not connected, cannot use attack');
      return;
    }

    const socket = this.socketService.getSocket()!;
    socket.emit(GameClientEvents.USE_ATTACK, {
      gameId,
      playerId,
      targetPlayerId,
    });
    console.log('📤 Emitting useAttack:', {
      gameId,
      playerId,
      targetPlayerId,
    });
  }

  emitTargetChanged(roomId: string, targetPlayerId: string): void {
    if (!this.socketService.isConnected()) return;
    const socket = this.socketService.getSocket()!;
    socket.emit(GameClientEvents.TARGET_CHANGED, { roomId, targetPlayerId });
  }

  /**
   * Use the active power-up (E). Player-variant power-ups apply to the user;
   * opponent-variant power-ups are sent to the selected target.
   */
  usePowerUp(gameId: string, playerId: string, targetPlayerId?: string): void {
    if (!this.socketService.isConnected()) {
      console.error('❌ Socket not connected, cannot use power-up');
      return;
    }

    const socket = this.socketService.getSocket()!;
    socket.emit(GameClientEvents.USE_POWER_UP, {
      gameId,
      playerId,
      targetPlayerId,
    });
    console.log('📤 Emitting usePowerUp:', { gameId, playerId, targetPlayerId });
  }

  /**
   * Switch the active power-up slot (Q).
   */
  switchPowerUp(gameId: string, playerId: string): void {
    if (!this.socketService.isConnected()) {
      console.error('❌ Socket not connected, cannot switch power-up');
      return;
    }

    const socket = this.socketService.getSocket()!;
    socket.emit(GameClientEvents.SWITCH_POWER_UP, { gameId, playerId });
    console.log('📤 Emitting switchPowerUp:', { gameId, playerId });
  }

  /**
   * Emit player_ready event to server
   */
  emitPlayerReady(roomId: string): void {
    if (!this.socketService.isConnected()) {
      console.error('❌ Socket not connected, cannot emit player_ready');
      return;
    }

    const socket = this.socketService.getSocket()!;
    socket.emit(ReadyClientEvents.PLAYER_READY, { roomId });
    console.log('📤 Emitting player_ready:', { roomId });
  }

  /**
   * Request a rematch
   */
  requestRematch(roomId: string): void {
    if (!this.socketService.isConnected()) {
      console.error('❌ Socket not connected, cannot request rematch');
      return;
    }

    const socket = this.socketService.getSocket()!;
    socket.emit(GameClientEvents.REMATCH, { roomId });
    console.log('📤 Emitting rematch', { roomId });
  }

  /**
   * Create a new room
   */
  createRoom(
    roomName: string,
    password?: string,
    settings?: Partial<GameSettings>
  ): void {
    if (!this.socketService.isConnected()) {
      console.error('❌ Socket not connected, cannot create room');
      return;
    }

    // The room will be bound to this socket's current server token — make sure
    // localStorage reflects it so a later RECONNECT uses the matching token.
    this.commitPendingToken();

    const socket = this.socketService.getSocket()!;
    const payload: CreateRoomDto = {
      roomName,
      password,
      username: this.usernameStorage.getUsername() ?? undefined,
      settings,
    };
    socket.emit(RoomClientEvents.CREATE_ROOM, payload);
    console.log('📤 Emitting createRoom:', payload);
  }

  /**
   * Join a room using its joinCode (capability-based auth).
   * Possessing the joinCode is sufficient authorisation.
   */
  joinRoom(joinCode: string): void {
    if (!this.socketService.isConnected()) {
      console.error('❌ Socket not connected, cannot join room');
      return;
    }

    // The room will be bound to this socket's current server token — make sure
    // localStorage reflects it so a later RECONNECT uses the matching token.
    this.commitPendingToken();

    const socket = this.socketService.getSocket()!;
    const payload: JoinRoomDto = { joinCode };
    socket.emit(RoomClientEvents.JOIN_ROOM, payload);
    console.log('📤 Emitting joinRoom:', payload);
  }

  /**
   * Verify password for a private room.
   * On success the server joins the player and responds with roomJoined (including joinCode).
   */
  verifyRoomPassword(roomId: string, password: string): void {
    if (!this.socketService.isConnected()) {
      console.error('❌ Socket not connected, cannot verify room password');
      return;
    }

    // Joining a private room also binds it to this socket's current token.
    this.commitPendingToken();

    const socket = this.socketService.getSocket()!;
    const payload: VerifyRoomPasswordDto = { roomId, password };
    socket.emit(RoomClientEvents.VERIFY_ROOM_PASSWORD, payload);
    console.log('📤 Emitting verifyRoomPassword:', { roomId });
  }

  /**
   * Request the current list of all rooms (one-time fetch)
   */
  getAllRooms(): void {
    if (!this.socketService.isConnected()) {
      console.error('❌ Socket not connected, cannot get rooms');
      return;
    }

    const socket = this.socketService.getSocket()!;
    socket.emit(RoomClientEvents.GET_ALL_ROOMS);
    console.log('📤 Emitting getAllRooms');
  }

  /**
   * Leave a room
   */
  leaveRoom(roomId: string): void {
    if (!this.socketService.isConnected()) {
      console.error('❌ Socket not connected, cannot leave room');
      return;
    }

    const socket = this.socketService.getSocket()!;
    const payload: LeaveRoomDto = { roomId };
    socket.emit(RoomClientEvents.LEAVE_ROOM, payload);
    console.log('📤 Emitting leaveRoom:', payload);
  }

  // ===== SERVER TO CLIENT OBSERVABLES =====

  get connectionStatus$(): Observable<ConnectionStatus> {
    return this.socketService.connectionStatus$;
  }

  get gameStateUpdates$(): Observable<GameState> {
    return this.gameStateUpdatesSubject.asObservable();
  }

  get serverTick$(): Observable<GameState> {
    return this.serverTickSubject.asObservable();
  }

  get matchTimer$(): Observable<number> {
    return this.matchTimerSubject.asObservable();
  }

  get playerReady$(): Observable<{ playerId: string; isReady: boolean }> {
    return this.playerReadySubject.asObservable();
  }

  get playerJoined$(): Observable<PlayerRoomEventDto> {
    return this.playerJoinedSubject.asObservable();
  }

  get playerLeft$(): Observable<PlayerRoomEventDto> {
    return this.playerLeftSubject.asObservable();
  }

  get attackIncoming$(): Observable<AttackData> {
    return this.attackIncomingSubject.asObservable();
  }

  get powerUpUsed$(): Observable<PowerUpData> {
    return this.powerUpUsedSubject.asObservable();
  }

  get gameStart$(): Observable<GameData> {
    return this.gameStartSubject.asObservable();
  }

  get gameOver$(): Observable<MatchEndResult> {
    return this.gameOverSubject.asObservable();
  }

  get roomCreated$(): Observable<RoomStateDto> {
    return this.roomCreatedSubject.asObservable();
  }

  get roomJoined$(): Observable<RoomStateDto> {
    return this.roomJoinedSubject.asObservable();
  }

  get roomError$(): Observable<{ type: string; message: string }> {
    return this.roomErrorSubject.asObservable();
  }

  get roomsUpdated$(): Observable<RoomStateDto[]> {
    return this.roomsUpdatedSubject.asObservable();
  }

  get readyUpdate$(): Observable<{
    readyPlayers: Record<string, boolean>;
    allReady?: boolean;
  }> {
    return this.readyUpdateSubject.asObservable();
  }

  get countdownStart$(): Observable<{ startsAt: number }> {
    return this.countdownStartSubject.asObservable();
  }

  get matchStart$(): Observable<MatchStartDto> {
    return this.matchStartSubject.asObservable();
  }

  get targetUpdate$(): Observable<{
    playerId: string;
    targetPlayerId: string;
  }> {
    return this.targetUpdateSubject.asObservable();
  }

  get rematchWaiting$(): Observable<{ requesters: string[] }> {
    return this.rematchWaitingSubject.asObservable();
  }

  get playerDisconnected$(): Observable<PlayerRoomEventDto> {
    return this.playerDisconnectedSubject.asObservable();
  }

  get playerReconnected$(): Observable<PlayerRoomEventDto> {
    return this.playerReconnectedSubject.asObservable();
  }

  get reconnectSuccess$(): Observable<ReconnectSuccessDto> {
    return this.reconnectSuccessSubject.asObservable();
  }

  get reconnectFailed$(): Observable<{ reason: string }> {
    return this.reconnectFailedSubject.asObservable();
  }

  get countdownCancelled$(): Observable<{
    roomId: string;
    room?: RoomStateDto;
  }> {
    return this.countdownCancelledSubject.asObservable();
  }

  // ===== PRIVATE METHODS =====

  private setupEventListeners(): void {
    const socket = this.socketService.getSocket();
    if (!socket) return;

    // Game state updates (high frequency)
    socket.on(GameServerEvents.GAME_STATE_UPDATE, (data: GameState) => {
      console.log('📥 Received gameStateUpdate:', data);
      this.gameStateUpdatesSubject.next(data);
    });

    // Server tick events (game loop updates)
    socket.on(GameServerEvents.SERVER_TICK, (data: GameState) => {
      console.log('📥 Received server_tick:', data);
      this.serverTickSubject.next(data);
    });

    // Match timer updates
    socket.on(
      GameServerEvents.MATCH_TIMER,
      (data: { timeRemaining: number }) => {
        console.log('📥 Received match_timer:', data);
        this.matchTimerSubject.next(data.timeRemaining);
      }
    );

    // Player ready status changes (legacy subject — superseded by readyUpdate$)
    socket.on(
      ReadyClientEvents.PLAYER_READY,
      (data: { playerId: string; isReady: boolean }) => {
        console.log('📥 Received player_ready:', data);
        this.playerReadySubject.next(data);
      }
    );

    // Player management events
    socket.on(GameServerEvents.PLAYER_JOINED, (data: PlayerRoomEventDto) => {
      console.log('📥 Received playerJoined:', data);
      this.playerJoinedSubject.next(data);
    });

    socket.on(GameServerEvents.PLAYER_LEFT, (data: PlayerRoomEventDto) => {
      console.log('📥 Received playerLeft:', data);
      this.playerLeftSubject.next(data);
    });

    // Game events
    socket.on(GameServerEvents.ATTACK_INCOMING, (data: AttackData) => {
      console.log('📥 Received attackIncoming:', data);
      this.attackIncomingSubject.next(data);
    });

    socket.on(GameServerEvents.POWER_UP_USED, (data: PowerUpData) => {
      console.log('📥 Received powerUpUsed:', data);
      this.powerUpUsedSubject.next(data);
    });

    socket.on(GameServerEvents.GAME_START, (data: GameData) => {
      console.log('📥 Received gameStart:', data);
      this.gameStartSubject.next(data);
    });

    socket.on(GameServerEvents.GAME_OVER, (data: MatchEndResult) => {
      console.log('📥 Received gameOver:', data);
      this.gameOverSubject.next(data);
    });

    // Room list events (initial fetch response + server push updates)
    socket.on(RoomServerEvents.ALL_ROOMS, (data: RoomStateDto[]) => {
      console.log('📥 Received allRooms:', data);
      this.roomsUpdatedSubject.next(data);
    });

    socket.on(RoomServerEvents.ROOMS_UPDATED, (data: RoomStateDto[]) => {
      console.log('📥 Received roomsUpdated:', data);
      this.roomsUpdatedSubject.next(data);
    });

    // Room events
    socket.on(RoomServerEvents.ROOM_CREATED, (data: RoomStateDto) => {
      console.log('📥 Received roomCreated:', data);
      this.roomCreatedSubject.next(data);
    });

    socket.on(RoomServerEvents.ROOM_JOINED, (data: RoomStateDto) => {
      console.log('📥 Received roomJoined:', data);
      this.roomJoinedSubject.next(data);
    });

    socket.on(
      RoomServerEvents.ERROR,
      (data: { type: string; message: string }) => {
        console.log('📥 Received room error:', data);
        this.roomErrorSubject.next(data);
      }
    );

    // Ready-up events
    socket.on(
      ReadyServerEvents.READY_UPDATE,
      (data: { readyPlayers: Record<string, boolean> }) => {
        console.log('📥 Received ready_update:', data);
        this.readyUpdateSubject.next(data);
      }
    );

    socket.on(
      ReadyServerEvents.COUNTDOWN_START,
      (data: { startsAt: number }) => {
        console.log('📥 Received countdown_start:', data);
        this.countdownStartSubject.next(data);
      }
    );

    socket.on(ReadyServerEvents.MATCH_START, (data: MatchStartDto) => {
      console.log('📥 Received match_start', data);
      this.matchStartSubject.next(data);
    });

    socket.on(
      GameServerEvents.TARGET_UPDATE,
      (data: { playerId: string; targetPlayerId: string }) => {
        this.targetUpdateSubject.next(data);
      }
    );

    socket.on(
      GameServerEvents.REMATCH_WAITING,
      (data: { requesters: string[] }) => {
        console.log('📥 Received rematch_waiting:', data);
        this.rematchWaitingSubject.next(data);
      }
    );

    // Disconnect / reconnect events
    socket.on(
      GameServerEvents.PLAYER_DISCONNECTED,
      (data: PlayerRoomEventDto) => {
        console.log('📥 Received playerDisconnected:', data);
        this.playerDisconnectedSubject.next(data);
      }
    );

    socket.on(
      GameServerEvents.PLAYER_RECONNECTED,
      (data: PlayerRoomEventDto) => {
        console.log('📥 Received playerReconnected:', data);
        this.playerReconnectedSubject.next(data);
      }
    );

    socket.on(
      GameServerEvents.RECONNECT_SUCCESS,
      (data: ReconnectSuccessDto) => {
        console.log('📥 Received reconnectSuccess:', data);
        this.cancelReconnectLoop();
        // Old token is still valid — discard the pending new one
        this.pendingNewToken = null;
        this.reconnectSuccessSubject.next(data);
      }
    );

    socket.on(GameServerEvents.RECONNECT_FAILED, (data: { reason: string }) => {
      console.log('📥 Received reconnectFailed:', data);
      this.cancelReconnectLoop();
      // For terminal failures, clear stored last room and swap in the pending
      // token so the next session starts fresh with a valid server-issued token.
      if (
        data.reason === 'room_gone' ||
        data.reason === 'room_mismatch' ||
        data.reason === 'token_not_found' ||
        data.reason === 'already_connected'
      ) {
        this.clearLastRoom();
        if (this.pendingNewToken) {
          localStorage.setItem(TOKEN_KEY, this.pendingNewToken);
          console.log(
            '🔑 Swapped to new token after terminal reconnect failure'
          );
        } else {
          localStorage.removeItem(TOKEN_KEY);
        }
        this.pendingNewToken = null;
      }
      this.reconnectFailedSubject.next(data);
    });

    socket.on(
      GameServerEvents.COUNTDOWN_CANCELLED,
      (data: { roomId: string }) => {
        console.log('📥 Received countdownCancelled:', data);
        this.countdownCancelledSubject.next(data);
      }
    );
  }

  ngOnDestroy(): void {
    this.cancelReconnectLoop();
    // Complete all subjects
    this.gameStateUpdatesSubject.complete();
    this.serverTickSubject.complete();
    this.matchTimerSubject.complete();
    this.playerReadySubject.complete();
    this.playerJoinedSubject.complete();
    this.playerLeftSubject.complete();
    this.attackIncomingSubject.complete();
    this.powerUpUsedSubject.complete();
    this.gameStartSubject.complete();
    this.gameOverSubject.complete();
    this.roomCreatedSubject.complete();
    this.roomJoinedSubject.complete();
    this.roomErrorSubject.complete();
    this.roomsUpdatedSubject.complete();
    this.readyUpdateSubject.complete();
    this.countdownStartSubject.complete();
    this.matchStartSubject.complete();
    this.rematchWaitingSubject.complete();
    this.targetUpdateSubject.complete();
    this.playerDisconnectedSubject.complete();
    this.playerReconnectedSubject.complete();
    this.reconnectSuccessSubject.complete();
    this.reconnectFailedSubject.complete();
    this.countdownCancelledSubject.complete();

    // Destroy socket service
    this.socketService.destroy();
  }
}
